'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
  useQueryStates
} from 'nuqs';
import { AlertTriangle, Loader2, Search, Sparkles, X } from 'lucide-react';

import PageContainer from '@/components/layout/page-container';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { buildDynamicFormColumns } from '@/lib/build-dynamic-form-columns';
import { dynamicFormQueries, type DynamicForm } from '@/features/dynamic-forms';

import { searchQueries } from '../services/search.queries';
import { resolveSearchFormIds } from '../utils/resolve-form-ids';
import {
  SEARCH_HASH,
  SEARCH_SUGGESTION_KEYS,
  STATUS_OPTIONS,
  REJECTED_OPTIONS,
  BOX_MEMBERSHIP_OPTIONS,
  DOC_TYPE_OPTIONS
} from '../constants';
import { useRecentSearches } from '../hooks/use-recent-searches';
import { SearchResultCard } from './search-result-card';
import { SearchFiltersBar } from './search-filters-bar';
import { ContainerDocumentsDialog } from './container-documents-dialog';
import { OcrSearchViewerDialog } from './ocr-search-viewer-dialog';
import type { SearchEntityType, SearchHit, SearchParams } from '../types';

// Stage filter options — reuse the registration pipeline stages, labelled via
// the existing `search.stage.*` keys.
const STAGE_VALUES = [
  'registration',
  'sticking',
  'review',
  'ready_for_packing',
  'reception',
  'sorting',
  'handover_scanning',
  'photo_scanning',
  'qr_review',
  'ocr',
  'qa1_ocr',
  'qa2_ocr',
  'qa3_ocr',
  'warehouse',
  'archived'
] as const;

// Generous upper bound for the page-count range slider — containers rarely
// exceed this, and the user can still type any number into the inputs.
const MAX_PAGE_COUNT = 1000;

// Entity-type scope chips. The user thinks "containers vs documents", not in
// engine internals, so the labels stay at that level. 'documents' means the
// OCR RESULTS — the records that carry extracted text, passages and a page to
// open. `page_index` rows are the container's document INDEX (one row per
// document slot, matching on its own record fields), so they answered the
// chip with cards that repeated the container number and had no document text
// behind them. They are still reachable under 'all'.
// 'all' sends no filter (every entity type).
const TYPE_FILTERS = ['all', 'containers', 'documents'] as const;
type TypeFilter = (typeof TYPE_FILTERS)[number];

const TYPE_FILTER_ENTITY_TYPES: Record<
  TypeFilter,
  SearchEntityType[] | undefined
> = {
  all: undefined,
  containers: ['registration'],
  documents: ['ocr_result']
};

/**
 * AI global search page. Renders a flat, per-document result list (one card
 * per hit). The query lives in the URL so a search is shareable, and only
 * runs on explicit submit. Clicking a card opens its parent container's
 * details + documents drill-in. Shares `GET /api/v1/search` with the Cmd+K
 * dialog.
 */
export function AiSearchView() {
  const t = useTranslations('search');
  const { recent, add: addRecent, clear: clearRecent } = useRecentSearches();

  const [q, setQ] = useQueryState('q', parseAsString.withDefault(''));
  const [type, setType] = useQueryState(
    'type',
    parseAsStringLiteral(TYPE_FILTERS).withDefault('all')
  );
  const [input, setInput] = useState(q);
  // Understanding runs with the search — which only fires on explicit submit
  // (Enter / Search / recent chip / suggestion) or on first load when the URL
  // already carries a query. Typing alone never searches.
  const [understand, setUnderstand] = useState(() => q.trim().length > 0);
  // Drill-in overlays live in the URL hash (#documents/<registrationId>,
  // #viewer/<ocrResultId>) so they deep-link, survive refresh, and close on
  // browser Back. Their payload is resolved from the already-loaded hit list.
  const {
    open: docsOpen,
    param: docsRegistrationId,
    openWith: openDocs,
    onOpenChange: closeDocs
  } = useHashParamDialog(SEARCH_HASH.documents);
  const {
    open: viewerOpen,
    param: viewerOcrResultId,
    openWith: openViewer,
    onOpenChange: closeViewer
  } = useHashParamDialog(SEARCH_HASH.viewer);

  // Keep the box in sync when q changes from outside the field (recent chip,
  // suggestion, browser back/forward). Typing updates `input` only; the query
  // (`q`) changes solely via submit, so nothing searches until the user submits.
  useEffect(() => {
    setInput(q);
  }, [q]);

  const enabled = q.trim().length > 0;

  // ─── Dynamic form selector (mirrors registration-listing) ──────────────────
  const { data: activeTemplate } = useQuery(
    dynamicFormQueries.activeByType('registration')
  );
  const { data: allFormsData } = useQuery(
    dynamicFormQueries.list({
      type: 'registration',
      status: 'active',
      per_page: 100
    })
  );
  const allForms = (allFormsData?.data ?? []) as DynamicForm[];

  const [urlFormId, setUrlFormId] = useQueryState(
    'dynamic_form_id',
    parseAsString.withDefault('')
  );
  const activeFormId = activeTemplate?.data?.id;

  // Two ids, deliberately: `displayFormId` drives the dynamic columns and falls
  // back to the active form, while `apiFormId` is the request filter and never
  // does — an unasked-for default there filtered every result to one form.
  const { displayFormId, apiFormId } = resolveSearchFormIds(
    urlFormId,
    activeFormId
  );

  const { data: selectedFormData } = useQuery({
    ...dynamicFormQueries.detail(displayFormId ?? 0),
    enabled: !!displayFormId && displayFormId !== activeFormId
  });

  const selectedForm =
    displayFormId === activeFormId
      ? activeTemplate?.data
      : selectedFormData?.data;
  const dynamicFormFields = useMemo(
    () => selectedForm?.fields ?? [],
    [selectedForm?.fields]
  );

  // ─── Filter columns ────────────────────────────────────────────────────────
  // Static filter-only columns (no display, no permission gate — always shown
  // for search.view users) plus dynamic-form columns (which keep their own
  // built-in `meta.permission`).
  const columns = useMemo<ColumnDef<SearchHit>[]>(() => {
    const staticColumns: ColumnDef<SearchHit>[] = [
      {
        id: 'current_stage',
        enableColumnFilter: true,
        enableHiding: false,
        size: 0,
        meta: {
          label: t('filters.stage'),
          variant: 'multiSelect' as const,
          icon: 'solar:routing-bold-duotone',
          options: STAGE_VALUES.map((value) => ({
            label: t.has(`stage.${value}`) ? t(`stage.${value}`) : value,
            value
          }))
        }
      },
      {
        id: 'status',
        enableColumnFilter: true,
        enableHiding: false,
        size: 0,
        meta: {
          label: t('filters.status'),
          variant: 'multiSelect' as const,
          icon: 'solar:checklist-minimalistic-bold-duotone',
          options: STATUS_OPTIONS.map((o) => ({
            label: t(`filters.${o.labelKey}`),
            value: o.value
          }))
        }
      },
      {
        id: 'is_rejected',
        enableColumnFilter: true,
        enableHiding: false,
        size: 0,
        meta: {
          label: t('filters.rejected'),
          variant: 'select' as const,
          icon: 'solar:close-circle-bold-duotone',
          options: REJECTED_OPTIONS.map((o) => ({
            label: t(`filters.${o.labelKey}`),
            value: o.value
          }))
        }
      },
      {
        id: 'box_membership',
        enableColumnFilter: true,
        enableHiding: false,
        size: 0,
        meta: {
          label: t('filters.boxMembership'),
          variant: 'select' as const,
          icon: 'solar:box-bold-duotone',
          options: BOX_MEMBERSHIP_OPTIONS.map((o) => ({
            label: t(`filters.${o.labelKey}`),
            value: o.value
          }))
        }
      },
      {
        id: 'created_at',
        enableColumnFilter: true,
        enableHiding: false,
        size: 0,
        meta: {
          label: t('filters.createdDate'),
          variant: 'dateRange' as const
        }
      },
      {
        id: 'page_count',
        enableColumnFilter: true,
        enableHiding: false,
        size: 0,
        meta: {
          label: t('filters.pageCount'),
          variant: 'range' as const,
          range: [0, MAX_PAGE_COUNT] as [number, number]
        }
      },
      {
        id: 'document_type',
        enableColumnFilter: true,
        enableHiding: false,
        size: 0,
        meta: {
          label: t('filters.documentType'),
          variant: 'multiSelect' as const,
          icon: 'solar:document-text-bold-duotone',
          options: DOC_TYPE_OPTIONS.map((o) => ({
            label: t.has(`docType.${o.labelKey}`)
              ? t(`docType.${o.labelKey}`)
              : o.value,
            value: o.value
          }))
        }
      }
    ];
    // Dynamic-form columns are typed against Registration, but this table holds
    // no data ([]) and only renders these columns' filter meta — their
    // accessor/cell never run. Reuse them for their filter wiring + permission.
    const dynamicColumns = buildDynamicFormColumns(
      dynamicFormFields
    ) as unknown as ColumnDef<SearchHit>[];
    return [...staticColumns, ...dynamicColumns];
  }, [t, dynamicFormFields]);

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<SearchHit>({
    columns: filteredColumns,
    data: [],
    pageCount: -1,
    urlNamespace: 'sf'
  });

  // ─── Read filter URL state and map into SearchParams ───────────────────────
  const selectionFields = useMemo(
    () => dynamicFormFields.filter((f) => f.type === 'selection_list'),
    [dynamicFormFields]
  );
  const dateFields = useMemo(
    () => dynamicFormFields.filter((f) => f.type === 'date'),
    [dynamicFormFields]
  );

  const dynamicSelectionParsers = useMemo(
    () =>
      Object.fromEntries(
        selectionFields.map((f) => [
          `form_field_${f.id}`,
          parseAsArrayOf(parseAsString, ',').withDefault([])
        ])
      ),
    [selectionFields]
  );
  const [dynamicSelectionFilters] = useQueryStates(dynamicSelectionParsers);

  const dynamicDateParsers = useMemo(
    () =>
      Object.fromEntries(
        dateFields.map((f) => [
          `form_field_${f.id}`,
          parseAsString.withDefault('')
        ])
      ),
    [dateFields]
  );
  const [dynamicDateFilters] = useQueryStates(dynamicDateParsers);

  const [urlStage] = useQueryState(
    'current_stage',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlStatus] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlIsRejected] = useQueryState(
    'is_rejected',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlBoxMembership] = useQueryState(
    'box_membership',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlCreatedAt] = useQueryState(
    'created_at',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlPageCount] = useQueryState(
    'page_count',
    parseAsString.withDefault('')
  );
  const [urlDocumentType] = useQueryState(
    'document_type',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );

  const filterParams = useMemo<Partial<SearchParams>>(() => {
    const params: Partial<SearchParams> = {};

    if (urlStage.length > 0) params.current_stage = urlStage;
    if (urlStatus.length > 0) params.status = urlStatus;
    if (urlDocumentType.length > 0) params.document_type = urlDocumentType;

    const rejected = String(urlIsRejected[0] ?? '');
    if (rejected === 'yes') params.is_rejected = true;
    else if (rejected === 'no') params.is_rejected = false;

    const box = String(urlBoxMembership[0] ?? '');
    if (box === 'in') params.in_box = true;
    else if (box === 'out') params.not_in_box = true;

    // created_at is stored as ms timestamps ("from,to").
    const toDateStr = (ts: string) => {
      const d = new Date(Number(ts));
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    if (urlCreatedAt[0])
      params.created_from = toDateStr(String(urlCreatedAt[0]));
    if (urlCreatedAt[1]) params.created_to = toDateStr(String(urlCreatedAt[1]));

    // page_count is stored as "min,max".
    if (urlPageCount) {
      const [min, max] = String(urlPageCount).split(',');
      if (min !== undefined && min !== '') params.min_page_count = Number(min);
      if (max !== undefined && max !== '') params.max_page_count = Number(max);
    }

    if (apiFormId !== undefined) params.dynamic_form_id = apiFormId;

    const responses: Record<string, string[]> = {};
    for (const [key, values] of Object.entries(dynamicSelectionFilters)) {
      if (Array.isArray(values) && values.length > 0) {
        responses[key.replace('form_field_', '')] = values;
      }
    }
    if (Object.keys(responses).length > 0) {
      params.form_responses_filter = responses;
    }

    const dateFilter: Record<string, { from?: string; to?: string }> = {};
    for (const [key, value] of Object.entries(dynamicDateFilters)) {
      if (!value) continue;
      const parts = String(value).split(',').filter(Boolean);
      const fieldId = key.replace('form_field_', '');
      const from = parts[0] ? toDateStr(parts[0]) : undefined;
      const to = parts[1] ? toDateStr(parts[1]) : undefined;
      if (from || to) dateFilter[fieldId] = { from, to };
    }
    if (Object.keys(dateFilter).length > 0) {
      params.form_date_filter = dateFilter;
    }

    return params;
  }, [
    urlStage,
    urlStatus,
    urlDocumentType,
    urlIsRejected,
    urlBoxMembership,
    urlCreatedAt,
    urlPageCount,
    apiFormId,
    dynamicSelectionFilters,
    dynamicDateFilters
  ]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError
  } = useInfiniteQuery(
    searchQueries.infinite(
      {
        q,
        per_page: 25,
        understand,
        entity_types: TYPE_FILTER_ENTITY_TYPES[type],
        ...filterParams
      },
      enabled
    )
  );

  const hits = useMemo(
    () => data?.pages.flatMap((page) => page.hits) ?? [],
    [data]
  );
  const totalHits = data?.pages[0]?.total ?? 0;
  const interpretation = data?.pages[0]?.interpretation ?? null;
  // Hidden per request — see the commented summary card below.
  // const aggregates = data?.pages[0]?.aggregates ?? null;
  const degraded = data?.pages[0]?.degraded ?? false;
  // Query-level ranking configuration: identical across pages, so it is read
  // off the first page like `interpretation` and `degraded` are, then threaded
  // into every card for the "why this rate" panel (OMA-639).
  const scoring = data?.pages[0]?.scoring ?? null;

  // The result ring shows `hit.rank_score` — the number that actually produced
  // the order — and falls back to `hit.similarity` for a hit the ranking model
  // never scored. Both are rendered under a label naming which one it is: the
  // cosine orders nothing, so an unlabelled circle beside a list position gets
  // read as the reason for that position, and the cosine routinely reads lower
  // on a card above a higher one (76 / 76 / 76 / 73 on a page the reranker had
  // ordered 97 / 78 / 73 / 68). The rank was previously confined to "Why this
  // result?" because the generative scorer left it null on most results — it
  // answered whole pages with nothing but 0.9 and 0.1, which the monotone guard
  // in ScoreBreakdown then nulled. The cross-encoder populates it, so it leads.
  // It is NOT `hit.score` either (`_text_match` is a packed integer, 0 for a
  // vector-only match). Neither number available → no ring.

  // Infinite scroll — fetch the next page when the sentinel nears the viewport.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, hits.length]);

  const submit = (term: string) => {
    const trimmed = term.trim();
    setInput(trimmed);
    setUnderstand(true);
    void setQ(trimmed || null);
    if (trimmed) addRecent(trimmed);
  };

  const handleFormChange = (value: string) => {
    // Picking the active form clears the URL param (it's the default);
    // 'all' and other forms are stored explicitly.
    if (value === String(activeFormId)) {
      void setUrlFormId(null);
    } else {
      void setUrlFormId(value || null);
    }
  };

  const openHit = (hit: SearchHit) => {
    // OCR hit: open the page viewer. Matched chunk IDs spotlight the bboxes
    // for content searches; filter-driven hits arrive without them and just
    // open the document on page 1 — the viewer's own search bar still works.
    if (hit.entity_type === 'ocr_result' && hit.entity_id != null) {
      openViewer(String(hit.entity_id));
      return;
    }

    // Everything else: open the parent container's drill-in.
    const registrationId =
      hit.registration_id ??
      (hit.entity_type === 'registration' ? hit.entity_id : null);
    if (registrationId == null) return;
    openDocs(String(registrationId));
  };

  // Resolve the hash id params back to the clicked hit's payload from the
  // already-loaded results (an off-page/refresh deep-link simply won't open —
  // no extra fetch is added here).
  const docsSelection = docsOpen
    ? (() => {
        const regId = Number(docsRegistrationId);
        if (!Number.isFinite(regId)) return null;
        const hit = hits.find(
          (h) =>
            h.registration_id === regId ||
            (h.entity_type === 'registration' && h.entity_id === regId)
        );
        return {
          registrationId: regId,
          containerNumber:
            hit?.container_number ?? hit?.identifiers?.[0] ?? null
        };
      })()
    : null;

  const ocrViewerSelection = viewerOpen
    ? (() => {
        const ocrId = Number(viewerOcrResultId);
        if (!Number.isFinite(ocrId)) return null;
        const hit = hits.find(
          (h) => h.entity_type === 'ocr_result' && h.entity_id === ocrId
        );
        return {
          ocrResultId: ocrId,
          matchedChunkIds: hit?.matched_chunk_ids ?? [],
          containerNumber:
            hit?.container_number ?? hit?.identifiers?.[0] ?? null,
          initialPageNumber: hit?.page_number ?? null
        };
      })()
    : null;

  return (
    <PageContainer
      pageTitle={t('pageTitle')}
      pageDescription={t('pageDescription')}
    >
      <div className='mx-auto flex w-full max-w-3xl flex-col gap-6'>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(input);
          }}
          className='relative'
        >
          <Search className='text-muted-foreground pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2' />
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('placeholder')}
            className='h-12 ps-10 pe-28 text-base'
            autoFocus
          />
          <div className='absolute end-2 top-1/2 flex -translate-y-1/2 items-center gap-1'>
            {isLoading && (
              <Loader2 className='text-muted-foreground me-1 size-4 animate-spin' />
            )}
            {input && (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8'
                onClick={() => {
                  setInput('');
                  void setQ(null);
                }}
                aria-label={t('clear')}
              >
                <X className='size-4' />
              </Button>
            )}
            <Button type='submit' size='sm' className='h-8'>
              {t('searchButton')}
            </Button>
          </div>
        </form>

        {enabled && (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-muted-foreground text-xs'>
              {t('typeFilter.label')}
            </span>
            {TYPE_FILTERS.map((value) => (
              <Badge
                key={value}
                asChild
                variant={type === value ? 'default' : 'secondary'}
                className='cursor-pointer rounded-full px-3 py-1'
              >
                <button type='button' onClick={() => void setType(value)}>
                  {t(`typeFilter.${value}`)}
                </button>
              </Badge>
            ))}
          </div>
        )}

        {enabled && (
          <SearchFiltersBar
            table={table}
            allForms={allForms}
            selectedFormId={urlFormId}
            onFormChange={handleFormChange}
          />
        )}

        {!enabled && (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-muted-foreground text-xs'>
              {t('tryLabel')}
            </span>
            {SEARCH_SUGGESTION_KEYS.map((key) => (
              <Badge
                key={key}
                asChild
                variant='secondary'
                className='hover:bg-secondary/70 cursor-pointer rounded-full px-3 py-1'
              >
                <button
                  type='button'
                  onClick={() => submit(t(`suggestions.${key}`))}
                >
                  <Sparkles className='size-3' />
                  {t(`suggestions.${key}`)}
                </button>
              </Badge>
            ))}
          </div>
        )}

        {degraded && (
          <div className='flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300'>
            <AlertTriangle className='mt-0.5 size-3.5 shrink-0' />
            <span>{t('degradedBanner')}</span>
          </div>
        )}

        {interpretation && (
          <div className='border-primary/30 bg-primary/5 flex flex-wrap items-center gap-2 rounded-md border p-3 text-xs'>
            <Sparkles className='text-primary size-3.5 shrink-0' />
            <span className='text-muted-foreground'>{t('interpretedAs')}</span>
            {interpretation.filters.is_rejected === true && (
              <Badge variant='secondary'>{t('interp.rejected')}</Badge>
            )}
            {interpretation.filters.is_rejected === false && (
              <Badge variant='secondary'>{t('interp.notRejected')}</Badge>
            )}
            {interpretation.filters.not_in_box && (
              <Badge variant='secondary'>{t('interp.notInBox')}</Badge>
            )}
            {interpretation.filters.in_box && (
              <Badge variant='secondary'>{t('interp.inBox')}</Badge>
            )}
            {interpretation.filters.current_stage?.map((s) => (
              <Badge key={`stage-${s}`} variant='secondary'>
                {t.has(`stage.${s}`) ? t(`stage.${s}`) : s}
              </Badge>
            ))}
            {interpretation.filters.stage_after && (
              <Badge variant='secondary'>
                {t('interp.passedStage', {
                  stage: t.has(`stage.${interpretation.filters.stage_after}`)
                    ? t(`stage.${interpretation.filters.stage_after}`)
                    : interpretation.filters.stage_after
                })}
              </Badge>
            )}
            {interpretation.filters.stage_before && (
              <Badge variant='secondary'>
                {t('interp.beforeStage', {
                  stage: t.has(`stage.${interpretation.filters.stage_before}`)
                    ? t(`stage.${interpretation.filters.stage_before}`)
                    : interpretation.filters.stage_before
                })}
              </Badge>
            )}
            {interpretation.filters.status?.map((s) => (
              <Badge key={`status-${s}`} variant='secondary'>
                {s}
              </Badge>
            ))}
            {interpretation.filters.client_name && (
              <Badge variant='secondary'>
                {interpretation.filters.client_name}
              </Badge>
            )}
            <button
              type='button'
              onClick={() => setUnderstand(false)}
              className='text-muted-foreground hover:text-foreground ms-auto underline'
            >
              {t('forceRawSearch')}
            </button>
          </div>
        )}

        {/* Hidden per request — search results summary card (total / in-box / rejected / by-stage aggregates).
        {aggregates && (
          <div className='bg-card space-y-3 rounded-lg border p-4'>
            <div className='flex items-baseline justify-between'>
              <span className='text-muted-foreground text-sm'>
                {t('summary.total')}
              </span>
              <span className='text-2xl font-bold tabular-nums'>
                {aggregates.total.toLocaleString()}
              </span>
            </div>
            <div className='flex flex-wrap gap-2 text-xs'>
              <Badge variant='secondary'>
                {t('summary.rejected')}: {aggregates.rejected_count}
              </Badge>
              <Badge variant='secondary'>
                {t('summary.inBox')}: {aggregates.in_box_count}
              </Badge>
            </div>
            {Object.keys(aggregates.by_stage).length > 0 && (
              <div className='space-y-1'>
                <span className='text-muted-foreground text-xs font-medium'>
                  {t('summary.byStage')}
                </span>
                <div className='flex flex-wrap gap-1.5'>
                  {Object.entries(aggregates.by_stage)
                    .sort((a, b) => b[1] - a[1])
                    .map(([stage, count]) => (
                      <Badge
                        key={`agg-stage-${stage}`}
                        variant='outline'
                        className='gap-1 text-[11px]'
                      >
                        {t.has(`stage.${stage}`) ? t(`stage.${stage}`) : stage}
                        <span className='font-semibold tabular-nums'>
                          {count}
                        </span>
                      </Badge>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
        */}

        {!enabled && (
          <div className='flex flex-col items-center gap-6 py-12 text-center'>
            <div className='bg-primary/10 text-primary flex size-14 items-center justify-center rounded-2xl'>
              <Sparkles className='size-7' />
            </div>
            <div className='space-y-1'>
              <h3 className='text-lg font-semibold'>{t('heroTitle')}</h3>
              <p className='text-muted-foreground mx-auto max-w-md text-sm'>
                {t('heroSubtitle')}
              </p>
            </div>
            {recent.length > 0 && (
              <div className='w-full max-w-md space-y-2 text-start'>
                <div className='flex items-center justify-between'>
                  <span className='text-muted-foreground text-xs font-medium'>
                    {t('recentSearches')}
                  </span>
                  <button
                    type='button'
                    onClick={clearRecent}
                    className='text-muted-foreground hover:text-foreground text-xs'
                  >
                    {t('clearRecent')}
                  </button>
                </div>
                <div className='flex flex-wrap gap-2'>
                  {recent.map((term) => (
                    <Badge
                      key={term}
                      variant='secondary'
                      className='hover:bg-accent cursor-pointer'
                      onClick={() => submit(term)}
                    >
                      {term}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {enabled && (
          <div className='flex flex-col gap-3'>
            <div className='flex items-center justify-between gap-2'>
              <div className='flex items-center gap-2 text-sm'>
                <Sparkles className='text-primary size-4' />
                <span className='font-semibold'>{t('resultsHeading')}</span>
              </div>
              <div className='text-muted-foreground flex items-center gap-2 text-xs'>
                {isLoading ? (
                  <>
                    <Loader2 className='size-3.5 animate-spin' />
                    <span>{t('searching')}</span>
                  </>
                ) : (
                  <>
                    <Badge variant='secondary' className='font-mono'>
                      {totalHits}
                    </Badge>
                    <span>{t('resultsCount', { count: totalHits })}</span>
                  </>
                )}
              </div>
            </div>

            {!isLoading && hits.length === 0 && (
              <div className='text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm'>
                {isError ? t('errorResults') : t('emptyResults')}
              </div>
            )}

            {hits.map((hit, i) => (
              <SearchResultCard
                key={`${hit.entity_type}-${hit.entity_id}-${i}`}
                hit={hit}
                onSelect={openHit}
                rankPercent={hit.rank_score ?? undefined}
                similarityPercent={hit.similarity ?? undefined}
                scoring={scoring}
              />
            ))}

            {hasNextPage && (
              <div
                ref={sentinelRef}
                className='flex items-center justify-center py-4'
              >
                {isFetchingNextPage && (
                  <Loader2 className='text-muted-foreground size-5 animate-spin' />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <ContainerDocumentsDialog
        registrationId={docsSelection?.registrationId ?? null}
        containerNumber={docsSelection?.containerNumber ?? null}
        open={docsOpen}
        onOpenChange={closeDocs}
      />

      <OcrSearchViewerDialog
        ocrResultId={ocrViewerSelection?.ocrResultId ?? null}
        matchedChunkIds={ocrViewerSelection?.matchedChunkIds ?? []}
        containerNumber={ocrViewerSelection?.containerNumber ?? null}
        initialPageNumber={ocrViewerSelection?.initialPageNumber ?? null}
        open={viewerOpen}
        onOpenChange={closeViewer}
      />
    </PageContainer>
  );
}
